import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Actions } from 'core-components/library/modalDialog'
import { MultiSelect } from 'core-components/library/multiSelect'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { Static } from 'core-components/types'
import { MarketApi } from 'core/classes/services/api/MarketApi'
import { CrudStoreItemCore } from 'core/classes/services/InitializationServiceCore'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'

import { UNIQUE_VALUES_LIST } from '../configs'

import { AOM_HP_ROLES, PBM_HP_ROLES } from './configs'
import { checkDuplicates } from './helpers/checkDuplicates'
import { filterHPRoles } from './helpers/filterHPRoles'
import { IHPRolesProps, THPRoles } from './models'

export const HPRoles: FC<IHPRolesProps> = ({ isDisabled, entryIdItem, onChangeField }) => {
  const entityHpRoles = entryIdItem.get('PR_HP_ROLE')
  const entityRegion: string = entryIdItem.get('Region')
  const entityMarket: string = entryIdItem.get('Market')
  const partnerId: string = entryIdItem.get('PartnerID')
  const contactId: string = entryIdItem.get('ContactID')

  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [deletedRole, setDeletedRole] = useState<string>('')
  const [currentHpRoles, setCurrentHpRoles] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [roles, setRoles] = useState<THPRoles[]>([])

  const { roleIds } = useSelector(selectUser)

  const isAOM = useMemo(() => {
    return roleIds.includes('AOM')
  }, [roleIds])

  const isPBM = useMemo(() => {
    return roleIds.includes('PBM')
  }, [roleIds])

  const hpAllRolesSource = useMemo(() => {
    const crud = CrudStore.get(CrudStoreItemCore.Dropdowns, 'HPRoles')
    crud.setRoot('HPRoleDropdown')
    crud.setSort([{ field: 'en', dir: 'asc' }])
    return crud
  }, [])

  const region = useMemo(() => {
    if (!entityRegion) {
      const res = MarketApi.getRegion(entityMarket).Region
      return res
    }
    return entityRegion
  }, [entityMarket, entityRegion])

  useEffect(() => {
    hpAllRolesSource.fetch().then((data) => {
      if (data) {
        const allRoles = data.items.map<THPRoles>((el) => ({
          key: el.EntryId,
          title: el.value,
          region: el.Relation,
        }))
        setRoles(allRoles.filter((el) => filterHPRoles(el, { isAOM, isPBM, region })))
      }
    })
  }, [hpAllRolesSource, isAOM, isPBM, region])

  const hpRolesByRegion = useMemo(() => {
    return new Crud({
      fetcher: new Static(roles),
      keyField: 'title',
      defaultPaging: {
        page: 0,
        pageSize: 30,
      },
    })
  }, [roles])

  const addNewRole = async (hpRoles: string[], checkingRole: string) => {
    setLoading(true)

    setCurrentHpRoles(hpRoles)
    if (UNIQUE_VALUES_LIST.includes(checkingRole) && !currentHpRoles.includes(checkingRole)) {
      const isDuplicated = await checkDuplicates({ contactId, partnerId, role: checkingRole })
      if (isDuplicated) {
        setShowConfirm(true)
      } else {
        onChangeField('PR_HP_ROLE', Array.from(hpRoles).join(';'))
      }
    } else {
      onChangeField('PR_HP_ROLE', Array.from(hpRoles).join(';'))
    }
    setLoading(false)
  }

  const deleteRole = async (hpRoles: string[]) => {
    setLoading(true)

    const isAOMRoleDelete =
      hpRoles.filter((el) => AOM_HP_ROLES.includes(el)).length !==
      currentHpRoles.filter((el) => AOM_HP_ROLES.includes(el)).length

    const isPBMRoleDelete =
      hpRoles.filter((el) => PBM_HP_ROLES.includes(el)).length !==
      currentHpRoles.filter((el) => PBM_HP_ROLES.includes(el)).length

    const isPublicRoleDeleted = !isAOMRoleDelete && !isPBMRoleDelete
    const isEditable =
      (isAOMRoleDelete && isAOM) || (isPBMRoleDelete && isPBM) || isPublicRoleDeleted

    if (isEditable) {
      const difference = currentHpRoles.filter((x) => !hpRoles.includes(x))
      if (UNIQUE_VALUES_LIST.includes(difference[0])) {
        const isDuplicated = await checkDuplicates({ contactId, partnerId, role: difference[0] })
        if (!isDuplicated) {
          setDeletedRole(difference[0])
        } else {
          setCurrentHpRoles(hpRoles)
          onChangeField('PR_HP_ROLE', Array.from(hpRoles).join(';'))
        }
      } else {
        setCurrentHpRoles(hpRoles)
        onChangeField('PR_HP_ROLE', Array.from(hpRoles).join(';'))
      }
    }
    setLoading(false)
  }

  useEffect(() => {
    const rolesArr = entityHpRoles ? entityHpRoles.split(';').filter(Boolean) : []
    setCurrentHpRoles(rolesArr)
  }, [entityHpRoles])

  if (loading) return <Loader />

  return (
    <>
      <MultiSelect
        label={'HP Role *'}
        disabled={isDisabled}
        source={hpRolesByRegion}
        caption={'title'}
        value={currentHpRoles}
        onChange={async (value: string[]) => {
          if (currentHpRoles.length > value.length) {
            await deleteRole(value)
          } else {
            await addNewRole(value, value[value.length - 1])
          }
        }}
      />
      <ModalDialog
        show={showConfirm}
        onClose={() => {
          const newRoles = [...currentHpRoles]
          newRoles.pop()
          setCurrentHpRoles(newRoles)
          setShowConfirm(false)
        }}
        size={'sm'}
        content={() => (
          <p style={{ lineHeight: 1.4, textAlign: 'center' }}>
            {`Partner already has the role: 
          ${currentHpRoles[currentHpRoles.length - 1]}. Do you want to replace?`}
          </p>
        )}
        footer={() => (
          <Actions
            position={'end'}
            items={[
              {
                id: 'yes',
                label: 'Yes',
                onClick: () => {
                  onChangeField('PR_HP_ROLE', Array.from(currentHpRoles).join(';'))
                  setShowConfirm(false)
                },
                variant: 'text',
                color: 'danger',
              },
              {
                id: 'cancel',
                label: 'Cancel',
                onClick: () => {
                  const newRoles = [...currentHpRoles]
                  newRoles.pop()
                  setCurrentHpRoles(newRoles)
                  setShowConfirm(false)
                },
              },
            ]}
          />
        )}
      />
      <ModalDialog
        show={!!deletedRole}
        onClose={() => setDeletedRole('')}
        size={'sm'}
        content={() => (
          <p style={{ lineHeight: 1.4, textAlign: 'center' }}>
            {` Please update a
          ${deletedRole}  for this Partner to go ahead with this Deletion.`}
          </p>
        )}
      />
    </>
  )
}
